import apiService from './api';
import { downloadBlob } from '../utils/downloadBlob';
import type {
  AuditFinding,
  AuditorEngagementInfo,
  AutoChecks,
  Checklist,
  ComplianceReport,
  FindingType,
  InvitePreview,
} from '../types/compliance';

// ============================================================================
// AUDITOR SERVICE — /api/auditor
//
// An external auditor is not a user, so this deliberately does NOT go through
// the shared axios client: its interceptors attach the signed-in user's token
// and try a refresh on 401, and an auditor has neither. It uses plain fetch
// with the auditor's own token, held in sessionStorage — gone when the tab
// closes, and never mixed with a user session in the same browser.
// ============================================================================

const TOKEN_KEY = 'factree.auditor.session';

export class AuditorError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'AuditorError';
    this.status = status;
    this.code = code;
  }
}

interface Session { token: string; expires_at: string; link: string }

function readSession(link: string): Session | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (s.link !== link || new Date(s.expires_at) <= new Date()) return null;
    return s;
  } catch {
    return null;
  }
}

class AuditorService {
  private token: string | null = null;

  private base() {
    return apiService.getBaseURL().replace(/\/$/, '');
  }

  private async call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const res = await fetch(`${this.base()}/auditor${path}`, { ...init, headers: { ...headers, ...(init.headers as Record<string, string> | undefined) } });
    const body = await res.json().catch(() => null) as { success?: boolean; data?: T; error?: { message?: string; code?: string } } | null;
    if (!res.ok || !body?.success) {
      throw new AuditorError(body?.error?.message || 'Something went wrong. Try again.', res.status, body?.error?.code);
    }
    return body.data as T;
  }

  /** Resume a session for this link, if one is still live in this tab. */
  resume(link: string): boolean {
    const s = readSession(link);
    this.token = s?.token ?? null;
    return !!s;
  }

  signOut() {
    this.token = null;
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* storage unavailable */ }
  }

  getInvite(link: string) {
    return this.call<InvitePreview>(`/invite/${encodeURIComponent(link)}`);
  }

  requestCode(link: string, email: string) {
    return this.call<{ message: string }>('/sign-in/code', { method: 'POST', body: JSON.stringify({ token: link, email }) });
  }

  async verifyCode(link: string, code: string) {
    const out = await this.call<{ token: string; expires_at: string }>('/sign-in/verify', {
      method: 'POST', body: JSON.stringify({ token: link, code }),
    });
    this.token = out.token;
    try {
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify({ ...out, link }));
    } catch { /* storage unavailable: the session lasts until reload */ }
  }

  getEngagement() {
    return this.call<AuditorEngagementInfo>('/engagement');
  }

  getReport() {
    return this.call<{ report: ComplianceReport; checks: AutoChecks; checklist: Checklist | null }>('/report');
  }

  markViewed(section: string) {
    return this.call<null>('/viewed', { method: 'POST', body: JSON.stringify({ section }) }).catch(() => null);
  }

  listFindings() {
    return this.call<AuditFinding[]>('/findings');
  }

  addFinding(data: { clause_ref: string | null; section: string | null; finding: string; finding_type: FindingType }) {
    return this.call<AuditFinding>('/findings', { method: 'POST', body: JSON.stringify(data) });
  }

  withdrawFinding(id: number) {
    return this.call<null>(`/findings/${id}`, { method: 'DELETE' });
  }

  async download(kind: 'pdf' | 'csv', filename: string) {
    const res = await fetch(`${this.base()}/auditor/${kind === 'pdf' ? 'report.pdf' : 'spray-register.csv'}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });
    if (!res.ok) throw new AuditorError('The download failed. Try again.', res.status);
    downloadBlob(await res.blob(), filename);
  }
}

export default new AuditorService();
